import { httpClient } from './httpClient';

export type HealthState = 'healthy' | 'approaching' | 'at_cap';

export interface LifetimeTotals {
  readonly commentsAnalysed: number;
  readonly postsAnalysed: number;
  readonly profilesCaptured: number;
  readonly profilesProcessed: number;
}

export interface AccountHealth {
  readonly accountId: number;
  readonly accountCreatedAt: string;
  readonly periodStartDate: string;
  readonly periodEndDate: string;
  readonly tokensLimit: number;
  readonly tokensUsed: number;
  readonly tokensUsedUpdatedAt: string;
  readonly healthState: HealthState;
  readonly projectedEndPercentage: number;
  readonly lifetimeTotals: LifetimeTotals;
}

/**
 * Per-account settings / feature flags. Map of `config_definitions.key →
 * resolved value` (default + override). Catalog lives in the BE (DB), so this
 * is intentionally typed as a flexible record — readers should use
 * `useFeature(key)` which falls back gracefully when a key is missing.
 */
export type AccountSettings = Record<string, boolean | number | string | null>;

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  language: string;
  timezone: string;
  avatarUrl?: string;
  accountHealth: AccountHealth | null;
  /** FEL-57 · Count of channels tracked by the user's account. Lives at the
   *  root (not inside accountHealth) so it survives null-health accounts. */
  channelsTracked?: number;
  isDemo?: boolean;
  settings?: AccountSettings;
  // AL-1861 · Fields the new Profile shows when the API returns them (BE
  // ticket for the basic profile adjustments). Optional: until the BE ships
  // them the page simply leaves those spots out.
  /** ISO timestamp of when the user was created. */
  memberSince?: string | null;
  /** ISO timestamp of the latest activity across the user's open sessions. */
  lastActiveAt?: string | null;
  activeSessionsCount?: number | null;
  /** The account (workspace) the user works in. */
  account?: { readonly id: number; readonly name: string } | null;
}

interface UpdateProfileData {
  name?: string;
  phone?: string | null;
  language?: string;
  timezone?: string;
  avatar?: File;
}

class UserService {
  private static instance: UserService;

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  async getProfile(): Promise<UserProfile> {
    try {
      const response = await httpClient.get<UserProfile>('/api/users/me');
      return response;
    } catch (error) {
      console.error('[UserService] Error fetching user profile:', error);
      throw error;
    }
  }

  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    try {
      // Create a clean object with only the provided fields
      const payload: Partial<UpdateProfileData> = {};
      if (data.name !== undefined) payload.name = data.name;
      if (data.language !== undefined) payload.language = data.language;
      if (data.phone !== undefined) payload.phone = data.phone;
      if (data.timezone !== undefined) payload.timezone = data.timezone;
      // Note: If you need to handle file uploads, you'll need a separate endpoint for that
      // as JSON can't handle file uploads directly

      const response = await httpClient.patch<UserProfile>('/api/users/me', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  async uploadAvatar(file: File): Promise<{ url: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await httpClient.post<{ url: string }>('/api/users/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    try {
      await httpClient.post('/api/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      await httpClient.delete('/api/users/delete');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  async getTimezones(): Promise<Record<string, Array<{ value: string; label: string }>>> {
    try {
      const response =
        await httpClient.get<Record<string, Array<{ value: string; label: string }>>>(
          '/api/users/timezones'
        );
      return response;
    } catch (error) {
      console.error('Error fetching timezones:', error);
      throw error;
    }
  }
}

export const userService = UserService.getInstance();
